import React, { useEffect, useState } from 'react';
import {
  superAdminGetProjects,
  superAdminGetTransactions,
  superAdminExportTransactions,
  superAdminFinancialSummary,
} from '../../api/api';

export default function SuperAdminReports() {
  const [projects, setProjects] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [summary, setSummary] = useState(null);

  const load = () => {
    superAdminGetProjects({ page: 1, limit: 10 }).then((r) => setProjects(r.data.data.projects || [])).catch(() => {});
    superAdminGetTransactions({ page: 1, limit: 20 }).then((r) => setTransactions(r.data.data.transactions || [])).catch(() => {});
    superAdminFinancialSummary().then((r) => setSummary(r.data.data || null)).catch(() => {});
  };

  useEffect(() => {
    load();
  }, []);

  const pickCurrency = () => {
    const rows = summary?.byCurrency || [];
    if (!rows.length) return 'PKR';
    return rows.length === 1 ? (rows[0].currency || 'PKR') : 'MIXED';
  };

  const formatMoney = (amount, currency = 'PKR') => `${currency} ${Number(amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}`;

  const exportExcel = async () => {
    const res = await superAdminExportTransactions({ format: 'excel' });
    const csv = res.data;
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'super-admin-transactions.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const exportPdfSource = async () => {
    const res = await superAdminExportTransactions({ format: 'pdf' });
    const count = res?.data?.data?.count || 0;
    alert(`PDF source data generated for ${count} rows. Use existing PDF utility to render printable report.`);
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="superadmin-title text-3xl font-bold">Analytics & Reports</h1>
          <p className="text-slate-500">Platform-wide project, transaction and financial intelligence</p>
        </div>
        <div className="space-x-2">
          <button onClick={exportExcel} className="ems-btn-secondary text-sm">Export Excel</button>
          <button onClick={exportPdfSource} className="ems-btn-primary text-sm">Export PDF</button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="superadmin-kpi rounded-2xl p-4">
          <p className="text-slate-500 text-xs">Total Income</p>
          <p className="text-2xl font-extrabold mt-2 text-slate-900">{formatMoney(summary?.summary?.total_income, pickCurrency())}</p>
        </div>
        <div className="superadmin-kpi rounded-2xl p-4">
          <p className="text-slate-500 text-xs">Total Expenses</p>
          <p className="text-2xl font-extrabold mt-2 text-slate-900">{formatMoney(summary?.summary?.total_expenses, pickCurrency())}</p>
        </div>
        <div className="superadmin-kpi rounded-2xl p-4">
          <p className="text-slate-500 text-xs">Net Position</p>
          <p className="text-2xl font-extrabold mt-2 text-slate-900">{formatMoney(summary?.summary?.net_profit, pickCurrency())}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <div className="superadmin-glass rounded-2xl p-4 overflow-auto">
          <p className="font-semibold mb-3">Recent Projects</p>
          <table className="ems-table min-w-[560px]">
            <thead>
              <tr className="text-left text-slate-500 border-b border-slate-100">
                <th className="py-2">Project</th>
                <th>Owner</th>
                <th>Status</th>
                <th>Budget</th>
              </tr>
            </thead>
            <tbody>
              {projects.map((p) => (
                <tr key={p.id} className="border-b border-slate-50">
                  <td className="py-2.5">{p.name}</td>
                  <td>{p.owner_name}</td>
                  <td>{p.status}</td>
                  <td>{formatMoney(p.budget || 0, p.currency || 'PKR')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="superadmin-glass rounded-2xl p-4 overflow-auto">
          <p className="font-semibold mb-3">Recent Transactions</p>
          <table className="ems-table min-w-[640px]">
            <thead>
              <tr className="text-left text-slate-500 border-b border-slate-100">
                <th className="py-2">Date</th>
                <th>User</th>
                <th>Type</th>
                <th>Amount</th>
                <th>Project</th>
              </tr>
            </thead>
            <tbody>
              {transactions.map((t) => (
                <tr key={t.id} className="border-b border-slate-50">
                  <td className="py-2.5">{t.date}</td>
                  <td>{t.user_name}</td>
                  <td>{t.type}</td>
                  <td>{formatMoney(t.amount, t.currency || 'PKR')}</td>
                  <td>{t.project_name || 'Unassigned'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
